import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, RadioGroup, FormControlLabel, Radio, Checkbox, Rating, CircularProgress } from '@mui/material';
import { AppLayout } from '../components/layout/AppLayout';
import { Loader } from '../components/common/Loader';
import { useChildrenSurveys } from '../hooks/useChildrenSurveys';
import { SurveyDetails, SurveyQuestion } from '../services/childrenSurveysService';

export const SurveysPage: React.FC = () => {
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [surveyDetails, setSurveyDetails] = useState<SurveyDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  const { surveys, isLoadingSurveys, surveysError, handleCompleteSurvey, getSurvey, saveSurveyAnswer } = useChildrenSurveys();

  const activeSurveys = surveys.filter((s) => s.isActive);

  const handleOpenSurvey = async (surveyId: string) => {
    setSelectedSurveyId(surveyId);
    setIsLoadingDetails(true);
    setAnswers({});
    try {
      const result = await getSurvey(surveyId);
      if (result && !result.alreadyCompleted) {
        setSurveyDetails(result.survey);
        setShowSurveyDialog(true);
      } else {
        alert('Этот опрос уже пройден');
      }
    } catch (error) {
      console.error('Error loading survey:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers({
      ...answers,
      [questionId]: Array.isArray(value) ? value : [value],
    });
  };

  const handleSubmit = async () => {
    if (!selectedSurveyId || !surveyDetails) return;

    // Save all answers first
    for (const question of surveyDetails.questions) {
      const questionAnswers = answers[question.id] || [];
      if (questionAnswers.length > 0) {
        await saveSurveyAnswer(selectedSurveyId, question.id, questionAnswers);
      }
    }

    // Complete the survey
    const result = await handleCompleteSurvey(selectedSurveyId);
    if (result.success) {
      setShowSurveyDialog(false);
      setSelectedSurveyId(null);
      setSurveyDetails(null);
    }
  };

  if (isLoadingSurveys && surveys.length === 0) {
    return <AppLayout title="Опросы"><Loader message="Загрузка..." /></AppLayout>;
  }

  if (surveysError) {
    return (
      <AppLayout title="Опросы">
        <Typography color="error">Ошибка: {surveysError}</Typography>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Опросы">
      <Box>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Доступные опросы ({activeSurveys.length})
        </Typography>

        {activeSurveys.length === 0 ? (
          <Typography color="textSecondary">Нет доступных опросов</Typography>
        ) : (
          <Grid container spacing={2}>
            {activeSurveys.map((survey) => (
              <Grid item xs={12} sm={6} key={survey.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{survey.title}</Typography>
                    {survey.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ marginY: 1 }}>
                        {survey.description}
                      </Typography>
                    )}
                    {survey.rewardPoints && (
                      <Typography variant="caption" color="primary">
                        +{survey.rewardPoints} баллов за прохождение
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ marginTop: 2 }}
                      onClick={() => handleOpenSurvey(survey.id)}
                    >
                      Пройти опрос
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog open={showSurveyDialog} onClose={() => setShowSurveyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{surveyDetails?.title}</DialogTitle>
        <DialogContent>
          {isLoadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
              {surveyDetails?.questions.map((question: SurveyQuestion) => (
                <Box key={question.id}>
                  <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                    {question.text}
                  </Typography>

                  {question.type === 'text' && (
                    <TextField
                      fullWidth
                      size="small"
                      value={answers[question.id]?.[0] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    />
                  )}

                  {question.type === 'single_choice' && (
                    <RadioGroup
                      value={answers[question.id]?.[0] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    >
                      {question.options?.map((option) => (
                        <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
                      ))}
                    </RadioGroup>
                  )}

                  {question.type === 'multiple_choice' && (
                    <Box>
                      {question.options?.map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              checked={(answers[question.id] || []).includes(option)}
                              onChange={(e) => {
                                const current = answers[question.id] || [];
                                handleAnswerChange(
                                  question.id,
                                  e.target.checked ? [...current, option] : current.filter((o) => o !== option)
                                );
                              }}
                            />
                          }
                          label={option}
                        />
                      ))}
                    </Box>
                  )}

                  {question.type === 'rating' && (
                    <Rating
                      value={parseInt(answers[question.id]?.[0] || '0')}
                      onChange={(e, v) => handleAnswerChange(question.id, String(v))}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSurveyDialog(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoadingDetails}>
            Завершить
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};